import type { AuthMode } from './auth/types';

/**
 * Typed accessors for Vite's `import.meta.env`. Fails loudly at startup if the
 * runtime is misconfigured (e.g. prod build with mock auth).
 */

export interface RuntimeEnv {
  readonly authMode: AuthMode;
  readonly apiBaseUrl: string;
  readonly isDev: boolean;
  readonly isProd: boolean;
  /**
   * Phase 1 — Narrative NLP suggestions (PTV2-1051 #5). Off by default;
   * flip per-tenant once the BE Text Mining endpoint lands. Read from
   * `VITE_NARRATIVE_NLP` ("true"/"1" → enabled).
   */
  readonly narrativeNlp: boolean;
  /**
   * Sentry error tracking (proto-assessment P2 "No Observability"). Wired into
   * the existing `reportError` / `logger` seam — see `src/observability/sentry.ts`.
   * Sentry stays disabled unless a DSN is configured, so local/mock dev is a
   * no-op by default. Never point a real DSN at a mock-auth build.
   */
  readonly sentry: SentryEnv;
}

export interface SentryEnv {
  /** `VITE_SENTRY_DSN`. Empty/undefined → Sentry is disabled entirely. */
  readonly dsn: string | undefined;
  /** `VITE_SENTRY_ENVIRONMENT` (falls back to the Vite mode). */
  readonly environment: string;
  /** `VITE_SENTRY_RELEASE` — optional build id for release health / source maps. */
  readonly release: string | undefined;
  /** `VITE_SENTRY_TRACES_SAMPLE_RATE` (0..1). Performance tracing sample rate. */
  readonly tracesSampleRate: number;
  /**
   * `VITE_SENTRY_TUNNEL` — same-origin path (e.g. `/monitoring`) to relay
   * event envelopes through instead of hitting `*.ingest.sentry.io` directly.
   * Set it to defeat ad-blockers / corporate egress filters that drop the
   * Sentry ingestion host. Requires a server relay at that path (the dev Vite
   * proxy provides one; production needs the BFF to). Empty/undefined → the SDK
   * sends directly to Sentry (default).
   */
  readonly tunnel: string | undefined;
}

function parseAuthMode(value: unknown): AuthMode {
  if (value === 'mock' || value === 'real') return value;
  throw new Error(
    `[new-ptv2] Invalid VITE_AUTH_MODE "${String(value)}". Expected "mock" or "real".`,
  );
}

export function readEnv(): RuntimeEnv {
  const isDev = import.meta.env.DEV;
  const isProd = import.meta.env.PROD;
  const authMode = parseAuthMode(import.meta.env.VITE_AUTH_MODE);
  const apiBaseUrl = import.meta.env.VITE_API_BASE_URL as string | undefined;

  if (typeof apiBaseUrl !== 'string' || apiBaseUrl.length === 0) {
    throw new Error('[new-ptv2] VITE_API_BASE_URL is required.');
  }

  // Use MODE (the literal vite mode string) not PROD — `vite build --mode development`
  // (our `pnpm build:dev` script) sets PROD=true but MODE='development', and we want
  // those artifacts to keep allowing mock auth.
  if (import.meta.env.MODE === 'production' && authMode !== 'real') {
    throw new Error(
      '[new-ptv2] Production runtime refuses VITE_AUTH_MODE !== "real". See PTV2-910.',
    );
  }

  const narrativeNlp = parseBoolFlag(import.meta.env.VITE_NARRATIVE_NLP);

  const sentry = readSentryEnv();

  return { authMode, apiBaseUrl, isDev, isProd, narrativeNlp, sentry };
}

function readSentryEnv(): SentryEnv {
  const rawDsn: unknown = import.meta.env.VITE_SENTRY_DSN;
  const dsn =
    typeof rawDsn === 'string' && rawDsn.trim().length > 0 ? rawDsn.trim() : undefined;

  const rawEnvironment: unknown = import.meta.env.VITE_SENTRY_ENVIRONMENT;
  const environment =
    typeof rawEnvironment === 'string' && rawEnvironment.length > 0
      ? rawEnvironment
      : import.meta.env.MODE;

  const rawRelease: unknown = import.meta.env.VITE_SENTRY_RELEASE;
  const release =
    typeof rawRelease === 'string' && rawRelease.length > 0 ? rawRelease : undefined;

  const rawTunnel: unknown = import.meta.env.VITE_SENTRY_TUNNEL;
  const tunnel =
    typeof rawTunnel === 'string' && rawTunnel.trim().length > 0 ? rawTunnel.trim() : undefined;

  return {
    dsn,
    environment,
    release,
    tracesSampleRate: parseSampleRate(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE),
    tunnel,
  };
}

/** Clamp a `"0.1"`-style env value into [0, 1]; unset/invalid → 0 (no tracing). */
function parseSampleRate(value: unknown): number {
  const parsed = typeof value === 'string' ? Number.parseFloat(value) : NaN;
  if (!Number.isFinite(parsed)) return 0;
  return Math.min(1, Math.max(0, parsed));
}

function parseBoolFlag(value: unknown): boolean {
  return value === 'true' || value === '1' || value === true;
}
